import {
  CONTROL_CHANNEL_LABEL,
  DATA_CHANNEL_LABEL,
  type IceCandidateInit,
  type PeerConnectionState,
} from '@flowshare/protocol';

export type OutboundSignal =
  | { type: 'description'; description: RTCSessionDescriptionInit }
  | { type: 'candidate'; candidate: IceCandidateInit | null };

export interface PeerChannels {
  control: RTCDataChannel;
  data: RTCDataChannel;
}

export interface PeerSessionOptions {
  remotePeerId: string;
  polite: boolean;
  configuration: RTCConfiguration;
  sendSignal: (signal: OutboundSignal) => void;
  onStateChange: (state: PeerConnectionState) => void;
  onChannels: (channels: PeerChannels) => void;
  onChannelsLost: () => void;
  onError?: (message: string) => void;
  createPeerConnection?: (configuration: RTCConfiguration) => RTCPeerConnection;
}

function mapState(state: RTCPeerConnectionState): PeerConnectionState {
  switch (state) {
    case 'new':
      return 'idle';
    case 'connecting':
      return 'connecting';
    case 'connected':
      return 'connected';
    case 'disconnected':
      return 'disconnected';
    case 'failed':
      return 'failed';
    case 'closed':
      return 'closed';
    default:
      return 'idle';
  }
}

/**
 * One RTCPeerConnection using the perfect negotiation pattern.
 *
 * The impolite peer (lexicographically smaller peer id) creates the data
 * channels and sends the first offer, so both sides agree on who initiates
 * without any extra handshake. Glare during ICE restarts is resolved by the
 * polite peer rolling back.
 */
export class PeerSession {
  private readonly pc: RTCPeerConnection;
  private control: RTCDataChannel | null = null;
  private data: RTCDataChannel | null = null;
  private ready = false;
  private destroyed = false;
  private makingOffer = false;
  private ignoreOffer = false;
  private isSettingRemoteAnswerPending = false;
  private pendingCandidates: RTCIceCandidateInit[] = [];
  private restartTimer: ReturnType<typeof setTimeout> | null = null;
  private restartCount = 0;

  constructor(private readonly options: PeerSessionOptions) {
    const factory =
      options.createPeerConnection ??
      ((configuration: RTCConfiguration) => new RTCPeerConnection(configuration));

    this.pc = factory(options.configuration);

    this.pc.onnegotiationneeded = () => {
      void this.onNegotiationNeeded();
    };
    this.pc.onicecandidate = (event) => {
      this.options.sendSignal({
        type: 'candidate',
        candidate: event.candidate
          ? {
              candidate: event.candidate.candidate,
              sdpMid: event.candidate.sdpMid,
              sdpMLineIndex: event.candidate.sdpMLineIndex,
              usernameFragment: event.candidate.usernameFragment,
            }
          : null,
      });
    };
    this.pc.onconnectionstatechange = () => {
      if (this.destroyed) return;
      const state = mapState(this.pc.connectionState);
      this.options.onStateChange(state);

      if (state === 'connected') {
        this.restartCount = 0;
        this.clearRestartTimer();
      } else if (state === 'disconnected') {
        this.scheduleRestart(3_000);
      } else if (state === 'failed') {
        this.scheduleRestart(500);
      }
    };
    this.pc.ondatachannel = (event) => {
      this.attachChannel(event.channel);
    };
  }

  start(): void {
    if (this.options.polite) return;
    this.createChannels();
  }

  private createChannels(): void {
    if (this.destroyed || this.control || this.data) return;
    const control = this.pc.createDataChannel(CONTROL_CHANNEL_LABEL, { ordered: true });
    const data = this.pc.createDataChannel(DATA_CHANNEL_LABEL, { ordered: true });
    this.attachChannel(control);
    this.attachChannel(data);
  }

  private attachChannel(channel: RTCDataChannel): void {
    if (this.destroyed) {
      channel.close();
      return;
    }

    const kind =
      channel.label === CONTROL_CHANNEL_LABEL
        ? 'control'
        : channel.label === DATA_CHANNEL_LABEL
          ? 'data'
          : null;

    if (!kind) {
      channel.close();
      return;
    }

    if ((kind === 'control' && this.control) || (kind === 'data' && this.data)) {
      channel.close();
      return;
    }

    channel.binaryType = 'arraybuffer';

    channel.addEventListener('open', () => this.maybeReady());
    channel.addEventListener('close', () => {
      if (!this.ready || this.destroyed) return;
      this.ready = false;
      this.options.onChannelsLost();
    });
    channel.addEventListener('error', () => {
      if (!this.destroyed) this.options.onError?.('Data channel error.');
    });

    if (kind === 'control') this.control = channel;
    else this.data = channel;

    if (channel.readyState === 'open') this.maybeReady();
  }

  private maybeReady(): void {
    if (this.ready || this.destroyed) return;
    if (this.control?.readyState !== 'open' || this.data?.readyState !== 'open') return;
    this.ready = true;
    this.options.onChannels({ control: this.control, data: this.data });
  }

  private async onNegotiationNeeded(): Promise<void> {
    if (this.destroyed) return;
    try {
      this.makingOffer = true;
      await this.pc.setLocalDescription();
      if (!this.pc.localDescription) return;
      this.options.sendSignal({
        type: 'description',
        description: {
          type: this.pc.localDescription.type as 'offer' | 'answer',
          sdp: this.pc.localDescription.sdp,
        },
      });
    } catch (error) {
      if (!this.destroyed) {
        this.options.onError?.(error instanceof Error ? error.message : 'Negotiation failed.');
      }
    } finally {
      this.makingOffer = false;
    }
  }

  async handleDescription(description: RTCSessionDescriptionInit): Promise<void> {
    if (this.destroyed) return;

    if (
      description.type === 'answer' &&
      this.pc.signalingState !== 'have-local-offer'
    ) {
      return;
    }

    const readyForOffer =
      !this.makingOffer &&
      (this.pc.signalingState === 'stable' || this.isSettingRemoteAnswerPending);
    const offerCollision = description.type === 'offer' && !readyForOffer;

    this.ignoreOffer = !this.options.polite && offerCollision;
    if (this.ignoreOffer) return;

    try {
      this.isSettingRemoteAnswerPending = description.type === 'answer';
      await this.pc.setRemoteDescription(description);
      this.isSettingRemoteAnswerPending = false;

      if (description.type === 'offer' && !this.destroyed) {
        await this.pc.setLocalDescription();
        if (this.pc.localDescription) {
          this.options.sendSignal({
            type: 'description',
            description: {
              type: this.pc.localDescription.type as 'offer' | 'answer',
              sdp: this.pc.localDescription.sdp,
            },
          });
        }
      }

      await this.flushCandidates();
    } catch (error) {
      this.isSettingRemoteAnswerPending = false;
      if (!this.destroyed) {
        this.options.onError?.(
          error instanceof Error ? error.message : 'Could not apply the remote description.',
        );
      }
    }
  }

  async handleCandidate(candidate: IceCandidateInit | null): Promise<void> {
    if (this.destroyed) return;
    if (!candidate) {
      // End-of-candidates is advisory; addIceCandidate(null) is not portable.
      return;
    }

    if (!this.pc.remoteDescription) {
      this.pendingCandidates.push(candidate);
      return;
    }

    if (this.ignoreOffer) return;

    try {
      await this.pc.addIceCandidate(candidate);
    } catch (error) {
      if (!this.ignoreOffer) {
        this.options.onError?.(
          error instanceof Error ? error.message : 'Could not add an ICE candidate.',
        );
      }
    }
  }

  private async flushCandidates(): Promise<void> {
    if (this.pendingCandidates.length === 0) return;
    const candidates = this.pendingCandidates.splice(0);
    for (const candidate of candidates) {
      try {
        await this.pc.addIceCandidate(candidate);
      } catch {
        // A candidate can expire if the network changed mid-negotiation.
      }
    }
  }

  private scheduleRestart(delayMs: number): void {
    if (this.destroyed || this.options.polite) return;
    this.clearRestartTimer();
    this.restartTimer = setTimeout(() => {
      this.restartTimer = null;
      if (this.destroyed) return;
      if (this.pc.connectionState === 'connected') return;
      if (this.restartCount >= 2) {
        this.options.onError?.('The peer connection could not be recovered.');
        this.close();
        return;
      }
      this.restartCount += 1;
      try {
        this.pc.restartIce();
      } catch {
        // Fall back to the signaling reconnect path.
      }
    }, delayMs);
  }

  private clearRestartTimer(): void {
    if (this.restartTimer !== null) {
      clearTimeout(this.restartTimer);
      this.restartTimer = null;
    }
  }

  async getRoundTripTime(): Promise<number | null> {
    if (this.destroyed || this.pc.connectionState !== 'connected') return null;
    try {
      const stats = await this.pc.getStats();
      let rtt: number | null = null;
      stats.forEach((report) => {
        if (
          report.type === 'candidate-pair' &&
          (report as RTCIceCandidatePairStats).nominated &&
          (report as RTCIceCandidatePairStats).state === 'succeeded'
        ) {
          const value = (report as RTCIceCandidatePairStats).currentRoundTripTime;
          if (typeof value === 'number') rtt = Math.round(value * 1000);
        }
      });
      return rtt;
    } catch {
      return null;
    }
  }

  close(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    this.clearRestartTimer();
    this.ready = false;

    try {
      this.control?.close();
      this.data?.close();
    } catch {
      // Channels may already be closed.
    }

    try {
      this.pc.close();
    } catch {
      // Already closed.
    }

    this.options.onStateChange('closed');
  }

  get isDestroyed(): boolean {
    return this.destroyed;
  }
}
